import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Switch } from 'components';
import TipCalculatorSiteSelector from 'components/SiteSelector/TipCalculatorSiteSelector';
import { H3, Text } from 'components/Typography';
import { appConfig } from 'config/app';
import { routeConstants } from 'routes';
import { SectionTitle, SettingsContent, SettingsContentLayout, SettingsPageHeader, SettingsSection, SettingsSectionContent, SettingsSectionHeader } from 'screens/Settings/components';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getEmployeesRequest } from 'store/Collections/Employees';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { selectSite, Site, updateSiteWithholdingsRequest } from 'store/Site';
import { IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeType, PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { DisabledWrapper, LoadingSkeleton, StyledLink, TipCalculationsContainer, Title, TitleRow } from '../styles';
import { StyledTabNav, StyledTabNavItem, TipCalculationsDescription } from '../TipCalculator/shared/styles';
import { DefaultSiteWithholdings, EmployeeWithholdings } from './components';

enum WithholdingsSettingsTab {
  DEFAULT = 'default',
  EMPLOYEES = 'employees',
}

interface IParams {
  tab?: WithholdingsSettingsTab;
}

const WithholdingsSettings = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { tab = WithholdingsSettingsTab.DEFAULT } = useParams<IParams>();

  const currentSite = useSelector(selectSite());
  const currentEmployeeSite = useSelector(selectCurrentEmployeeSite);
  const hasEditPermission = hasSitePermission(currentEmployeeSite?.permissions, PermissionType.SITE_WITHHOLDINGS_UPDATE);
  const { withholdings } = currentSite?.settings ?? {};
  const isWithholdingsAllowed = withholdings?.allowed;
  const showWithholdingsSettings = isWithholdingsAllowed;
  const isSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.GET_SITE_REQUEST));
  const isUpdatingWithholdings = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.UPDATE_WITHHOLDINGS_REQUEST));

  useEffect(() => {
    // Fetch employees to display in the employee withholdings tab
    if (currentSite?.id && isWithholdingsAllowed) {
      const excludeOrgAdminQuery = [{ key: 'type', operator: ApiQueryOperator.neq, value: EmployeeType.ADMIN }];
      dispatch(getEmployeesRequest(AffiliationType.SITES, currentSite.id, { limit: 0, sort: 'name', q: excludeOrgAdminQuery }));
    }
  }, [currentSite?.id, isWithholdingsAllowed, dispatch]);

  const toggleWithholdingsApply = () => {
    dispatch(updateSiteWithholdingsRequest(currentSite.id, { ...withholdings, apply: !withholdings?.apply }, { successMessage: `Withholdings will ${!withholdings?.apply ? 'be applied' : 'not be applied'} to employee payouts.` }));
  };

  const handleTabChange = (tab: WithholdingsSettingsTab) => {
    const pathname = `${routeConstants.private.settingsWithholdings}/${tab}`;
    history.push({ pathname });
  };

  const renderBlockedContent = () => (
    <DisabledWrapper>
      <Title>Withholdings Not Configured</Title>
      <Text size="small">
        This site does not currently allow for withholding to be configured.{' '}
        <StyledLink target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.SUPPORT}>
          Contact Kickfin Support
        </StyledLink>{' '}
        to learn more.
      </Text>
    </DisabledWrapper>
  );

  return (
    <SettingsContent data-cy="withholdings-settings">
      <SettingsPageHeader />

      <SettingsContentLayout>
        <SettingsSection>
          <SettingsSectionHeader>
            <H3>Withholdings</H3>
          </SettingsSectionHeader>

          <SettingsSectionContent light data-cy="withholdings-settings-content">
            {/* Site Selector */}
            <SectionTitle>Select Site</SectionTitle>
            <TipCalculatorSiteSelector />

            {!currentSite || isSiteLoading ? (
              <LoadingSkeleton />
            ) : showWithholdingsSettings ? (
              <>
                {/* Withholdings toggle */}
                <TipCalculationsContainer data-cy="withholdings-enable-container">
                  <TitleRow>
                    <Title fontWeight="medium">Enable Withholdings</Title>
                    <Switch checked={withholdings?.apply} onChange={toggleWithholdingsApply} disabled={!hasEditPermission || isUpdatingWithholdings} data-cy="withholdings-switch" />
                  </TitleRow>
                  <TipCalculationsDescription size="xsmall">Enable tip withholdings on payouts.</TipCalculationsDescription>
                </TipCalculationsContainer>

                {/* Settings */}
                <TipCalculationsContainer>
                  <StyledTabNav data-cy="withholdings-tab-nav">
                    <StyledTabNavItem label="Default Withholding" isActive={tab === WithholdingsSettingsTab.DEFAULT} onClick={() => handleTabChange(WithholdingsSettingsTab.DEFAULT)} />
                    <StyledTabNavItem label="Employee Withholdings" isActive={tab === WithholdingsSettingsTab.EMPLOYEES} onClick={() => handleTabChange(WithholdingsSettingsTab.EMPLOYEES)} />
                  </StyledTabNav>

                  {/* Default Site Withholdings */}
                  {tab === WithholdingsSettingsTab.DEFAULT && <DefaultSiteWithholdings currentSite={currentSite} withholdings={withholdings} isLoading={isUpdatingWithholdings} readOnly={!hasEditPermission} />}

                  {/* Custom Employee Withholdings */}
                  {tab === WithholdingsSettingsTab.EMPLOYEES && <EmployeeWithholdings withholdings={withholdings} isLoading={isUpdatingWithholdings} readOnly={!hasEditPermission} />}
                </TipCalculationsContainer>
              </>
            ) : (
              renderBlockedContent()
            )}
          </SettingsSectionContent>
        </SettingsSection>
      </SettingsContentLayout>
    </SettingsContent>
  );
};

export default WithholdingsSettings;
